// Conversão wall-time ↔ epoch em fuso arbitrário, com Intl puro (sem lib de
// datas). Compartilhado porque a mesma regra vale em mais de um app:
//  - CRM (#185): data/hora digitada é interpretada no fuso do QUADRO.
//  - `apps/app` (etiquetas): o filtro de período do histórico é interpretado no
//    fuso do TENANT — o dia vai de 00:00:00 a 23:59:59.999 lá, não no fuso do
//    navegador de quem está olhando.
// Epoch ms segue sendo o único formato persistido.

// Intl.DateTimeFormat é caro de construir; a agenda formata por evento.
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(timeZone);
  if (!fmt) {
    // `hourCycle: 'h23'` é obrigatório: 'h24' devolve "24" para meia-noite e
    // quebraria o Date.UTC abaixo.
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, fmt);
  }
  return fmt;
}

/** Wall-time que o instante `ms` representa no fuso, como epoch "fingido" UTC. */
function wallAsUtc(ms: number, timeZone: string): number {
  const parts = partsFormatter(timeZone).formatToParts(new Date(ms));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * `'YYYY-MM-DD'` + `'HH:mm[:ss]'` no fuso dado → epoch ms; `null` se inválido.
 *
 * Algoritmo chute+correção: trata a wall-time desejada como se fosse UTC e
 * corrige pelo offset observado via Intl. Duas iterações convergem para
 * qualquer offset fixo e para transições de DST (a 1ª acerta o offset
 * aproximado; a 2ª corrige se o chute caiu do outro lado da transição).
 * Casos de DST (irrelevantes para America/Sao_Paulo, sem DST desde 2019):
 * - gap (hora inexistente no spring-forward): retorna o instante deslocado
 *   pelo tamanho do gap — determinístico;
 * - overlap (hora ambígua no fall-back): resolve para um dos dois offsets,
 *   também determinístico; não desambiguamos.
 */
export function wallTimeToEpoch(date: string, time: string, timeZone: string): number | null {
  const dm = DATE_RE.exec(date);
  const tm = TIME_RE.exec(time);
  if (!dm || !tm) return null;
  const [, y, mo, d] = dm;
  const [, h, mi, s] = tm;
  const desired = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s ?? '0'));
  if (!Number.isFinite(desired)) return null;
  let guess = desired;
  try {
    for (let i = 0; i < 2; i += 1) {
      guess += desired - wallAsUtc(guess, timeZone);
    }
  } catch {
    // timeZone inválido (RangeError do Intl) — não temos como interpretar.
    return null;
  }
  return guess;
}

/** Inverso, para prefill de modais: epoch → `{date, time}` no fuso dado. */
export function epochToWallTime(ms: number, timeZone: string): { date: string; time: string } {
  const parts = partsFormatter(timeZone).formatToParts(new Date(ms));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
}

const HOUR_MS = 60 * 60 * 1000;

/** Dia-calendário (`'YYYY-MM-DD'`) que o instante `ms` representa no fuso. */
function wallDate(ms: number, timeZone: string): string {
  return epochToWallTime(ms, timeZone).date;
}

/**
 * **Primeiro** instante cujo dia-calendário no fuso é `date` — a definição
 * canônica de "início do dia" (#241).
 *
 * Não dá para pedir `00:00:00` ao [wallTimeToEpoch]: no gap de DST à
 * meia-noite a hora não existe e o chute+correção converge para o lado errado
 * da transição — em `America/Santiago` 2026-09-06 (24:00 → 01:00) ele devolve
 * 23:00 do dia 5, uma hora ANTES do dia começar.
 *
 * Busca binária em segundos sobre um predicado monotônico (o dia-calendário
 * nunca retrocede: mesmo no fall-back de 24:00 → 23:00 o relógio volta dentro
 * do mesmo dia). Âncora no meio-dia, que nenhum fuso real torna inexistente ou
 * ambíguo; transições são segundo-alinhadas, então a busca é exata.
 */
function firstInstantOfDateInTz(date: string, timeZone: string): number | null {
  const noon = wallTimeToEpoch(date, '12:00:00', timeZone);
  if (noon == null) return null;
  // Fuso exótico o bastante para o meio-dia cair em outro dia: não arriscamos.
  if (wallDate(noon, timeZone) !== date) return null;
  // O início do dia está em (noon − 18h, noon]: 12h de dia + folga para o
  // maior deslocamento de offset que existe (Lord Howe/Chatham inclusive).
  let lo = noon - 18 * HOUR_MS; // wallDate(lo) < date
  let hi = noon; // wallDate(hi) === date
  if (wallDate(lo, timeZone) >= date) return null;
  while (hi - lo > 1000) {
    const mid = lo + Math.floor((hi - lo) / 2000) * 1000;
    if (mid <= lo) break;
    if (wallDate(mid, timeZone) >= date) hi = mid;
    else lo = mid;
  }
  return hi;
}

/** Primeiro instante (00:00:00, ou 01:00 no gap de DST) daquela data no fuso. */
export function startOfDayInTz(date: string, timeZone: string): number | null {
  return firstInstantOfDateInTz(date, timeZone);
}

/**
 * **Último** instante daquela data no fuso: `startOfDay(dia+1) − 1ms`. Limite
 * **inclusivo** (`<= to`) em filtros de período e prazo de tarefa no CRM.
 *
 * Definir pelo início do dia seguinte (em vez de pedir `23:59:59`) resolve o
 * overlap do fall-back por construção: em `America/Santiago` 2026-04-05, onde
 * 23:00–24:00 acontece duas vezes, o fim do dia é a **segunda** passagem —
 * antes caía na primeira e uma etiqueta vencendo às 23:30 pulava para "amanhã"
 * enquanto o badge de data ainda mostrava hoje (#241).
 */
export function endOfDayInTz(date: string, timeZone: string): number | null {
  const nextStart = firstInstantOfDateInTz(shiftDate(date, 1), timeZone);
  return nextStart != null ? nextStart - 1 : null;
}

// ---------------------------------------------------------------------------
// Recortes de dia a partir de um instante (`nowMs`) no fuso do tenant.
// Usados pelos filtros client-side de etiquetas (chips Hoje/Amanhã/Semana,
// alertas da Home, presets *_TODAY): o "dia" é o do estabelecimento, não o do
// device de quem está olhando. Fuso inválido/vazio → recorte no fuso local do
// device (mesmo fallback dos formatters do #237); nunca lançam.
//
// Semântica canônica (#241), idêntica ao `TenantDayWindow.kt` do Android:
// "amanhã" e "fim do dia" são sempre dias-calendário, nunca `+24h` — no dia da
// transição de DST o dia tem 23h ou 25h e a aritmética fixa erra 1h.

const DAY_MS = 24 * 60 * 60 * 1000;

function localStartOfDayMillis(nowMs: number): number {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function localEndOfDayMillis(nowMs: number): number {
  const d = new Date(nowMs);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function localEndOfWeekMillis(nowMs: number): number {
  const d = new Date(nowMs);
  d.setHours(23, 59, 59, 999);
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return d.getTime();
}

/** Data `'YYYY-MM-DD'` deslocada `days` dias, em aritmética UTC pura (DST-safe). */
function shiftDate(date: string, days: number): string {
  const m = DATE_RE.exec(date);
  if (!m) return date;
  const [, y, mo, d] = m;
  const shifted = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)) + days * DAY_MS);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(shifted.getUTCFullYear())}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** Início do dia que contém `nowMs` no fuso dado. */
export function startOfDayMillisInTz(nowMs: number, timeZone: string): number {
  return startOfDayShiftedMillisInTz(nowMs, timeZone, 0) ?? localStartOfDayMillis(nowMs);
}

/**
 * Início do dia **seguinte** ao que contém `nowMs` — fronteira exclusiva das
 * janelas "de hoje" (`[startOfDay, startOfNextDay)`). Não é `+24h`: no dia da
 * transição de DST o dia tem 23h ou 25h (#241).
 */
export function startOfNextDayMillisInTz(nowMs: number, timeZone: string): number {
  return startOfDayShiftedMillisInTz(nowMs, timeZone, 1) ?? localStartOfDayMillis(nowMs) + DAY_MS;
}

/** Último instante do dia que contém `nowMs` no fuso dado. */
export function endOfDayMillisInTz(nowMs: number, timeZone: string): number {
  const start = startOfDayShiftedMillisInTz(nowMs, timeZone, 1);
  return start != null ? start - 1 : localEndOfDayMillis(nowMs);
}

/**
 * Último instante do dia **seguinte** ao que contém `nowMs` — fim da janela
 * "amanhã" (`(endOfDay, endOfTomorrow]`). Antes era `fim de hoje + 24h`, que
 * deslocava 1h nos dias de transição de DST (#241).
 */
export function endOfTomorrowMillisInTz(nowMs: number, timeZone: string): number {
  const start = startOfDayShiftedMillisInTz(nowMs, timeZone, 2);
  return start != null ? start - 1 : localEndOfDayMillis(nowMs) + DAY_MS;
}

/**
 * Último instante do domingo da semana que contém `nowMs` no fuso dado — a
 * semana dos filtros termina domingo; se o dia do fuso já é domingo, equivale
 * ao fim de hoje.
 */
export function endOfWeekMillisInTz(nowMs: number, timeZone: string): number {
  try {
    const { date } = epochToWallTime(nowMs, timeZone);
    const m = DATE_RE.exec(date);
    if (!m) return localEndOfWeekMillis(nowMs);
    const [, y, mo, d] = m;
    const dow = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay();
    const mondayAfter = shiftDate(date, ((7 - dow) % 7) + 1);
    const start = firstInstantOfDateInTz(mondayAfter, timeZone);
    return start != null ? start - 1 : localEndOfWeekMillis(nowMs);
  } catch {
    return localEndOfWeekMillis(nowMs);
  }
}

/** Início do dia `nowMs + days` dias-calendário no fuso; `null` se não der. */
function startOfDayShiftedMillisInTz(nowMs: number, timeZone: string, days: number): number | null {
  try {
    const { date } = epochToWallTime(nowMs, timeZone);
    return firstInstantOfDateInTz(days === 0 ? date : shiftDate(date, days), timeZone);
  } catch {
    return null;
  }
}
